import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

export type DayOfWeek =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export interface CleaningArea {
  id: string;
  name: string;
  icon: string;
  description: string | null;
  scheduledDays: DayOfWeek[];
  products: string | null;
  procedure: string | null;
  disabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SetCleaningAreaActiveStateRequest {
  active: boolean;
  label?: string;
}

export interface CleaningAreaWithStatus {
  id: string;
  name: string;
  icon: string;
  description: string | null;
  products: string | null;
  procedure: string | null;
  cleaningStatus: 'PENDING' | 'COMPLETED' | 'NON_COMPLIANT';
  lastCleaningRecord: {
    id: string;
    /** `YYYY-MM-DD` — the record day; NC/attachment links must carry this,
     * not the `completedAt` timestamp. */
    date: string;
    completedAt: string;
    isCompliant: boolean;
  } | null;
  isScheduledForToday: boolean;
  closure: {
    label: string | null;
    endDate: string | null;
  } | null;
}

export interface CleaningAreasByDateResponse {
  date: string;
  dayOfWeek: DayOfWeek;
  areas: CleaningAreaWithStatus[];
}

export interface CleaningRecord {
  id: string;
  cleaningAreaId: string;
  areaName: string;
  date: string;
  completedAt: string;
  isCompliant: boolean;
  notes: string | null;
  performedBy: string | null;
  performerName: string;
}

export interface CreateCleaningRecordRequest {
  date: string;
  isCompliant?: boolean;
  notes?: string;
  nonComplianceReason?: string;
  signatureStorageKey?: string;
}

export interface CorrectCleaningRecordRequest {
  isCompliant: boolean;
  notes?: string;
  reason?: string;
}

export interface CreateCleaningAreaRequest {
  name: string;
  icon: string;
  description?: string;
  scheduledDays: DayOfWeek[];
  products?: string;
  procedure?: string;
}

/** `null` clears a text field; an omitted key leaves it unchanged. */
export interface UpdateCleaningAreaRequest {
  name?: string;
  icon?: string;
  description?: string | null;
  scheduledDays?: DayOfWeek[];
  products?: string | null;
  procedure?: string | null;
}

type CleaningDayStatus = 'complete' | 'partial' | 'nonCompliant' | 'missing' | 'future';

interface CleaningCalendarDayInfo {
  status: CleaningDayStatus;
  scheduledCount: number;
  completedCount: number;
}

export interface CleaningCalendarResponse {
  calendar: Record<string, CleaningDayStatus>;
  days: Record<string, CleaningCalendarDayInfo>;
}

export interface CleaningAreaClosurePeriod {
  id: string;
  cleaningAreaId: string;
  startDate: string;
  endDate: string | null; // null = periodo aperto (area spenta via toggle finché non riaccesa)
  recurringYearly: boolean;
  label: string | null;
  createdByUserId: string | null;
}

export interface CreateClosurePeriodRequest {
  startDate: string;
  endDate: string;
  recurringYearly?: boolean;
  label?: string;
}

export type UpdateClosurePeriodRequest = Partial<CreateClosurePeriodRequest>;

export interface ICleaningAreaService {
  findAll(): Promise<CleaningArea[]>;
  findById(id: string): Promise<CleaningArea>;
  findByDate(date: string): Promise<CleaningAreasByDateResponse>;
  create(dto: CreateCleaningAreaRequest): Promise<CleaningArea>;
  update(id: string, dto: UpdateCleaningAreaRequest): Promise<CleaningArea>;
  delete(id: string): Promise<void>;
  setActiveState(id: string, dto: SetCleaningAreaActiveStateRequest): Promise<CleaningArea>;
  getCalendar(from: string, to: string): Promise<CleaningCalendarResponse>;
  findRecordsByAreaId(areaId: string, from?: string, to?: string): Promise<CleaningRecord[]>;
  createRecord(areaId: string, dto: CreateCleaningRecordRequest): Promise<CleaningRecord>;
  correctRecord(recordId: string, dto: CorrectCleaningRecordRequest): Promise<CleaningRecord>;
  listClosurePeriods(areaId: string): Promise<CleaningAreaClosurePeriod[]>;
  /** One call for the whole list page: periods of every non-deleted area of the tenant. */
  listAllClosurePeriods(): Promise<CleaningAreaClosurePeriod[]>;
  createClosurePeriod(
    areaId: string,
    dto: CreateClosurePeriodRequest,
  ): Promise<CleaningAreaClosurePeriod>;
  updateClosurePeriod(
    areaId: string,
    periodId: string,
    dto: UpdateClosurePeriodRequest,
  ): Promise<CleaningAreaClosurePeriod>;
  deleteClosurePeriod(areaId: string, periodId: string): Promise<void>;
}

@Injectable()
class CleaningAreaService implements ICleaningAreaService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  async findAll(): Promise<CleaningArea[]> {
    return this.httpService.get<CleaningArea[]>('cleaning-areas');
  }

  async findById(id: string): Promise<CleaningArea> {
    return this.httpService.get<CleaningArea>(`cleaning-areas/${id}`);
  }

  async findByDate(date: string): Promise<CleaningAreasByDateResponse> {
    return this.httpService.get<CleaningAreasByDateResponse>(`cleaning-areas/date/${date}`);
  }

  async getCalendar(from: string, to: string): Promise<CleaningCalendarResponse> {
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    return this.httpService.get<CleaningCalendarResponse>(
      `cleaning-areas/calendar?${params.toString()}`,
    );
  }

  async create(dto: CreateCleaningAreaRequest): Promise<CleaningArea> {
    return this.httpService.post<CleaningArea>('cleaning-areas', dto);
  }

  async update(id: string, dto: UpdateCleaningAreaRequest): Promise<CleaningArea> {
    return this.httpService.put<CleaningArea>(`cleaning-areas/${id}`, dto);
  }

  async delete(id: string): Promise<void> {
    await this.httpService.delete(`cleaning-areas/${id}`);
  }

  async setActiveState(id: string, dto: SetCleaningAreaActiveStateRequest): Promise<CleaningArea> {
    return this.httpService.patch<CleaningArea>(`cleaning-areas/${id}/active-state`, dto);
  }

  async findRecordsByAreaId(areaId: string, from?: string, to?: string): Promise<CleaningRecord[]> {
    const params = new URLSearchParams();
    if (from) {
      params.set('from', from);
    }
    if (to) {
      params.set('to', to);
    }
    return this.httpService.get<CleaningRecord[]>(
      `cleaning-areas/${areaId}/records?${params.toString()}`,
    );
  }

  async createRecord(areaId: string, dto: CreateCleaningRecordRequest): Promise<CleaningRecord> {
    return this.httpService.post<CleaningRecord>(`cleaning-areas/${areaId}/record`, dto);
  }

  async correctRecord(
    recordId: string,
    dto: CorrectCleaningRecordRequest,
  ): Promise<CleaningRecord> {
    return this.httpService.post<CleaningRecord>(
      `cleaning-areas/records/${recordId}/corrections`,
      dto,
    );
  }

  async listClosurePeriods(areaId: string): Promise<CleaningAreaClosurePeriod[]> {
    return this.httpService.get<CleaningAreaClosurePeriod[]>(
      `cleaning-areas/${areaId}/closure-periods`,
    );
  }

  async listAllClosurePeriods(): Promise<CleaningAreaClosurePeriod[]> {
    return this.httpService.get<CleaningAreaClosurePeriod[]>('cleaning-areas/closure-periods');
  }

  async createClosurePeriod(
    areaId: string,
    dto: CreateClosurePeriodRequest,
  ): Promise<CleaningAreaClosurePeriod> {
    return this.httpService.post<CleaningAreaClosurePeriod>(
      `cleaning-areas/${areaId}/closure-periods`,
      dto,
    );
  }

  async updateClosurePeriod(
    areaId: string,
    periodId: string,
    dto: UpdateClosurePeriodRequest,
  ): Promise<CleaningAreaClosurePeriod> {
    return this.httpService.patch<CleaningAreaClosurePeriod>(
      `cleaning-areas/${areaId}/closure-periods/${periodId}`,
      dto,
    );
  }

  async deleteClosurePeriod(areaId: string, periodId: string): Promise<void> {
    await this.httpService.delete(`cleaning-areas/${areaId}/closure-periods/${periodId}`);
  }
}

export { CleaningAreaService };
